// src/pages/admin/Brands.tsx
//
// Admin Brands directory: every brand in the FM ledger with a
// placeholder logo, product count, and a per-row "Hide from public
// catalog" toggle. Mirrors the public /catalog Brands view but
// adds the toggle that the visibility backend already supports
// (see shopdb/visibility.py + /api/admin/visibility/hide).
//
// Backend is the source of truth for hide state. The page reads
// the brand list from /api/brands and the hidden set from the
// admin visibility stats endpoint on mount, then keeps a local
// mirror of the hidden set so toggles are instant. Each toggle
// PATCHes /api/admin/visibility/hide (or /unhide) and updates the
// mirror on success.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Eye, EyeOff, ImagePlus, Search, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAuth } from "@/components/auth/AuthContext";
import {
  authHeaders,
  deleteBrandLogo,
  fetchVisibilityStats,
  uploadBrandLogo,
  type BrandRow,
} from "@/lib/api";
import { brandCatalogHref, brandLogoTileClass } from "@/lib/brandLogos";
import { cn } from "@/lib/cn";

interface VisibilityStats {
  customers: { total: number; hidden: number };
  brands: { total: number; hidden: number };
  products: { total: number; hidden: number };
}

export function AdminBrands() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [logoBusyIds, setLogoBusyIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  // Hidden per-row file inputs, keyed by brand_id, so the Add/Replace
  // buttons can open the file picker without a visible input.
  const logoFileInputs = useRef(new Map<number, HTMLInputElement>());

  // Initial load: brand list + which ones are hidden.
  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    setError("");
    Promise.all([
      // include_hidden=true so we can show a "Hidden from catalog"
      // badge for rows that the operator hid by mistake. The public
      // /catalog still excludes them (server filters by is_staff on
      // the include_hidden flag).
      fetch("/api/brands?limit=200&include_hidden=true", { headers: authHeaders() })
        .then(async (r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return (await r.json()) as { rows: BrandRow[] };
        }),
      fetchVisibilityStats(),
    ])
      .then(([b, stats]) => {
        setBrands(b.rows);
        // The /stats endpoint only returns counts, not ids. We
        // also need the per-id list, so fetch the per-id list
        // whenever there's anything hidden; on a fresh demo DB
        // the count is 0 and we skip the round-trip.
        const s = stats as VisibilityStats;
        if (s.brands.hidden > 0) {
          return fetch(
            "/api/admin/visibility/hidden?entity_type=brand",
            { headers: authHeaders() },
          )
            .then((r) => (r.ok ? r.json() : { ids: [] }))
            .then((j) => {
              const ids = Array.isArray(j?.ids) ? j.ids : [];
              setHiddenIds(new Set(ids.map((x: unknown) => Number(x))));
              return undefined;
            });
        }
        return undefined;
      })
      .catch((e) => setError(String(e instanceof Error ? e.message : e)));
  }, [auth.isReady, auth.isAuthenticated]);

  const filtered = useMemo(() => {
    // Sort alphabetically so the operator can find a brand by
    // name without scrolling through 77 entries; match the public
    // /catalog brands view. With hidden rows included, an admin can
    // also locate a row they hid by mistake.
    const q = query.toLowerCase();
    return brands
      .filter((b) => !q || b.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  }, [brands, query]);

  const toggle = useCallback(
    async (b: BrandRow) => {
      if (b.brand_id == null) return;
      const bid = b.brand_id;
      const wasHidden = hiddenIds.has(bid);
      setBusyIds((s) => new Set(s).add(bid));
      // Optimistic
      setHiddenIds((s) => {
        const next = new Set(s);
        if (wasHidden) next.delete(bid); else next.add(bid);
        return next;
      });
      try {
        const endpoint = wasHidden
          ? "/api/admin/visibility/unhide"
          : "/api/admin/visibility/hide";
        const r = await fetch(endpoint, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            entity_type: "brand",
            scope: "selected",
            ids: [bid],
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      } catch (e) {
        // Revert on failure
        setHiddenIds((s) => {
          const next = new Set(s);
          if (wasHidden) next.add(bid); else next.delete(bid);
          return next;
        });
        setError(String(e instanceof Error ? e.message : e));
      } finally {
        setBusyIds((s) => {
          const next = new Set(s);
          next.delete(bid);
          return next;
        });
      }
    },
    [authHeaders, hiddenIds],
  );

  // Update one brand row in place after a logo mutation.
  const patchBrand = useCallback((bid: number, patch: Partial<BrandRow>) => {
    setBrands((rows) =>
      rows.map((r) => (r.brand_id === bid ? { ...r, ...patch } : r)),
    );
  }, []);

  const setLogoBusy = useCallback((bid: number, busy: boolean) => {
    setLogoBusyIds((s) => {
      const next = new Set(s);
      if (busy) next.add(bid); else next.delete(bid);
      return next;
    });
  }, []);

  const uploadLogo = useCallback(
    async (b: BrandRow, file: File) => {
      if (b.brand_id == null) return;
      const bid = b.brand_id;
      setError("");
      setLogoBusy(bid, true);
      try {
        const res = await uploadBrandLogo(bid, file);
        // Cache-bust the displayed image: the asset URL is stable per
        // slug+extension, so a replaced logo would otherwise serve the
        // browser-cached previous image.
        patchBrand(bid, {
          has_logo: true,
          logo_url: `${res.logo_url}?v=${Date.now()}`,
        });
      } catch (e) {
        setError(String(e instanceof Error ? e.message : e));
      } finally {
        setLogoBusy(bid, false);
      }
    },
    [patchBrand, setLogoBusy],
  );

  const removeLogo = useCallback(
    async (b: BrandRow) => {
      if (b.brand_id == null) return;
      if (
        !window.confirm(
          t("admin.brands.removeLogoConfirm", { name: b.name }),
        )
      ) {
        return;
      }
      const bid = b.brand_id;
      setError("");
      setLogoBusy(bid, true);
      try {
        await deleteBrandLogo(bid);
        // logo_url null → the monogram fallback renders, matching the
        // placeholder behaviour the confirm copy promises.
        patchBrand(bid, { has_logo: false, logo_url: null });
      } catch (e) {
        setError(String(e instanceof Error ? e.message : e));
      } finally {
        setLogoBusy(bid, false);
      }
    },
    [patchBrand, setLogoBusy],
  );

  const stats = useMemo(() => {
    const total = brands.length;
    const hidden = hiddenIds.size;
    return { total, hidden, visible: total - hidden };
  }, [brands, hiddenIds]);

  if (!auth.isReady) {
    return (
      <p className="py-8 text-center text-sm text-fg-tertiary">{t("admin.brands.loading")}</p>
    );
  }
  if (!auth.isAuthenticated) {
    return (
      <EmptyState
        title={t("admin.brands.signInRequired")}
        description={t("admin.brands.signInRequiredDescription")}
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
            {t("admin.brands.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.brands.stats", {
              total: stats.total,
              visible: stats.visible,
              hidden: stats.hidden,
            })}
          </p>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.brands.searchPlaceholder")}
              className="pl-8"
              aria-label={t("admin.brands.searchAriaLabel")}
            />
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title={t(
            query ? "admin.brands.emptyNoMatch" : "admin.brands.emptyNoBrands",
          )}
          description={t(
            query
              ? "admin.brands.emptyNoMatchDescription"
              : "admin.brands.emptyNoBrandsDescription",
          )}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => {
            const isHidden = b.brand_id != null && hiddenIds.has(b.brand_id);
            const logoBusy =
              b.brand_id != null && logoBusyIds.has(b.brand_id);
            const monogram = b.name.trim().charAt(0).toUpperCase() || "?";
            return (
              <Card
                key={b.name}
                className={cn(
                  "overflow-hidden transition-colors",
                  isHidden ? "opacity-60" : "hover:border-benchmark-teal/40",
                )}
              >
                <CardContent className="space-y-3 p-5">
                  <div className="flex items-start gap-3">
                    <div className={brandLogoTileClass(b.logo_url)}>
                      {b.logo_url ? (
                        <img
                          src={b.logo_url}
                          alt={b.name}
                          width={80}
                          height={48}
                          loading="lazy"
                          className="h-full w-full object-contain"
                        />
                      ) : null}
                      <span
                        className="font-510 text-base text-fg-quaternary"
                        style={b.logo_url ? { display: "none" } : undefined}
                        aria-hidden
                      >
                        {monogram}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-510 text-fg-primary">
                        {b.name}
                      </h3>
                      <p className="mt-0.5 text-xs text-fg-tertiary">
                        {t("admin.brands.productCount", { count: b.count })}
                      </p>
                      {/* The one thing this directory could not do:
                          open the brand's products (support report).
                          Deep-links the catalog filtered to the brand. */}
                      <Link
                        to={brandCatalogHref(b.name)}
                        aria-label={t("admin.brands.viewProductsAria", { name: b.name })}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-510 text-benchmark-teal-bright hover:underline"
                      >
                        {t("admin.brands.viewProducts")}
                      </Link>
                      {isHidden && (
                        <Badge size="sm" className="mt-1">
                          {t("admin.brands.hiddenFromCatalog")}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 border-t border-border pt-2">
                    <Button
                      size="sm"
                      variant={isHidden ? "secondary" : "ghost"}
                      onClick={() => void toggle(b)}
                      disabled={b.brand_id == null || busyIds.has(b.brand_id)}
                      className="flex-1"
                      aria-label={t(
                        isHidden ? "admin.brands.restoreAria" : "admin.brands.hideAria",
                        { name: b.name },
                      )}
                    >
                      {isHidden ? (
                        <Eye className="h-3 w-3" strokeWidth={1.5} />
                      ) : (
                        <EyeOff className="h-3 w-3" strokeWidth={1.5} />
                      )}
                      {t(isHidden ? "admin.brands.show" : "admin.brands.hide")}
                    </Button>
                    {b.brand_id != null && (
                      <>
                        <input
                          ref={(el) => {
                            const bid = b.brand_id;
                            if (bid == null) return;
                            if (el) logoFileInputs.current.set(bid, el);
                            else logoFileInputs.current.delete(bid);
                          }}
                          type="file"
                          accept=".svg,.png,.jpg,.jpeg,.webp,.gif"
                          className="hidden"
                          aria-label={t("admin.brands.chooseLogoAria", { name: b.name })}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            // Reset so picking the same file twice still
                            // fires a change event.
                            e.target.value = "";
                            if (f) void uploadLogo(b, f);
                          }}
                        />
                        {b.has_logo ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                b.brand_id != null &&
                                logoFileInputs.current.get(b.brand_id)?.click()
                              }
                              disabled={logoBusy}
                              aria-label={t("admin.brands.replaceLogoAria", { name: b.name })}
                            >
                              <ImagePlus className="h-3 w-3" strokeWidth={1.5} />
                              {t(logoBusy ? "admin.brands.working" : "admin.brands.replace")}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => void removeLogo(b)}
                              disabled={logoBusy}
                              aria-label={t("admin.brands.removeLogoAria", { name: b.name })}
                            >
                              <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                              {t("admin.brands.remove")}
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              b.brand_id != null &&
                              logoFileInputs.current.get(b.brand_id)?.click()
                            }
                            disabled={logoBusy}
                            aria-label={t("admin.brands.addLogoAria", { name: b.name })}
                          >
                            <ImagePlus className="h-3 w-3" strokeWidth={1.5} />
                            {t(logoBusy ? "admin.brands.working" : "admin.brands.addLogo")}
                          </Button>
                        )}
                      </>
                    )}
                    {b.logo_url && (
                      <a
                        href={b.logo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-fg-quaternary hover:text-fg-secondary"
                        title={t("admin.brands.openLogoInNewTab")}
                      >
                        {t("admin.brands.logoLink")}
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
